import { dayjs } from '../../support/helpers/dayjs.ts';
import { useIonPicker } from '@ionic/react';
import { useBreakpoints } from '../../support/hooks/useBreakpoints.tsx';
import { DownChevronIcon } from '../icons/Icons.tsx';
import { formatDate, formatTime } from '../../support/helpers/format.ts';
import { Color } from '../common/index.ts';
import { Button } from '../button/Button.tsx';

// build a list of dates with mission date selected
const buildDateList = (selectedValue: string, fromMoment: any, toMoment: any) => {
    const options: {text: string, value: string, selected: boolean}[] = [];
    while(!fromMoment.isAfter(toMoment)) {
        const text = fromMoment.format('ddd MMM, D');
        const value = fromMoment.format('YYYY-MM-DD');
        options.push({ text, value, selected: value === selectedValue });
        fromMoment = fromMoment.add(1, 'd');
    }
    return options;
};

// build list of hours (1 - 12)
const buildHourList = (selectedValue: number) => {
    return Array.from(Array(12).keys()).map(i => {
        const hour = (i === 0 ? 12 : i);
        return { text: String(hour), value: hour, selected: selectedValue === hour };
    });
};

// build list of minutes in 5-minute increments (00 - 55)
const buildMinuteList = (selectedValue: number, increment: number) => {
    return Array.from(Array(60/increment).keys()).map(i => {
        const minute = i * increment;

        // selectedValue should already be on an increment boundary, but if not, match with nearest
        const selected = selectedValue > (minute - increment/2) && selectedValue < (minute + increment/2);

        return { text: String(minute).padStart(2, '0'), value: minute, selected };
    });
};

// build AM/PM list
const buildAPList = (selectedValue: string) => {
    return [
        { text: 'AM', value: 'AM', selected: selectedValue === 'AM' },
        { text: 'PM', value: 'PM', selected: selectedValue === 'PM' }
    ];
};

const buildDateTimePicker = (dateTime: string, timezone: string, fromDate: string, toDate: string, minuteIncrement: number, animated: boolean, onChange: (value: string) => void) => {
    // if no date range specified, assume we don't show the date
    const timeOnly = (!fromDate && !toDate);

    const localStartTime = dayjs(dateTime).tz(timezone);
    const fromMoment = dayjs(fromDate).tz(timezone).startOf('d');
    const toMoment = dayjs(toDate).tz(timezone).startOf('d');

    // these all need to be regenerated every time the picker is presented, or the UX glitches out
    const dateList = buildDateList(localStartTime.format('YYYY-MM-DD'), fromMoment, toMoment); // e.g. '2022-03-01'
    const hourList = buildHourList(Number(localStartTime.format('h'))); // 1 - 12
    const minuteList = buildMinuteList(localStartTime.minute(), minuteIncrement); // 0 - 59
    const apList = buildAPList(localStartTime.format('A')); // 'AM' or 'PM'

    const columns = [
        {
            name: 'hour',
            columnWidth: '30px',
            options: hourList,
            selectedIndex: hourList.findIndex(h => h.selected)
        },
        {
            name: 'min',
            columnWidth: '30px',
            options: minuteList,
            selectedIndex: minuteList.findIndex(m => m.selected)
        },
        {
            name: 'ap',
            columnWidth: '60px',
            options: apList,
            selectedIndex: apList.findIndex(ap => ap.selected)
        }
    ];

    if (!timeOnly) {
        columns.unshift(
            {
                name: 'date',
                columnWidth: '160px',
                options: dateList,
                selectedIndex: dateList.findIndex(d => d.selected)
            }
        );
    }

    const buttons = [
        {
            text: 'Cancel',
            cssClass: 'picker-cancel'
        },
        {
            text: 'Done',
            cssClass: 'picker-done',
            handler: (selected: any) => {

                // build string dayjs can parse in format "2022-03-20 16:30"
                const { date, hour, min, ap } = selected;
                const h0 = hour.value === 12 ? 0 : hour.value;
                const hour24 = String(h0 + (ap.value === 'PM' ? 12 : 0)).padStart(2, '0');
                const dateTimeString = `${date?.value || dayjs().format('YYYY-MM-DD')} ${hour24}:${min.text}`;
                const shiftToLocalTime = true;
                const result = dayjs(dateTimeString).tz(timezone, shiftToLocalTime);
                onChange(result.toISOString());
            }
        }
    ];

    return { columns, buttons, animated };
};

export const DateTimePicker: React.FC<{ dateTime: string, timezone: string, fromDate?: string, toDate?: string, minuteIncrement?: number, color?: Color, onChange: (dateTime: string) => void, disabled?: boolean }> = ({ dateTime, timezone, fromDate, toDate, minuteIncrement, color, onChange, disabled }) => {
    const [present] = useIonPicker();
    const { isLargerScreen } = useBreakpoints();

    // if no date range specified, assume we don't show the date or timezone
    const timeOnly = (!fromDate && !toDate);

    // for backward-compatibility reasons, when we set a color make it bold
    const wrapperStyle = {
        color: `var(--ion-color-${color || 'dark'})`,
        fontWeight: color ? 'bold' : undefined,
        fontSize: 16
    };

    return (
        <Button disabled={disabled} minWidth={0} fill="clear" dataSet={{type: 'date-time-picker', tz: timezone}} onClick={() => present(buildDateTimePicker(dateTime, timezone, fromDate!, toDate!, minuteIncrement || 1, !isLargerScreen, onChange))}>
            <span style={wrapperStyle}>
                {!timeOnly ? `${formatDate({ date: dateTime, timezone })} ` : ''}{formatTime({ time: dateTime, timezone, withTimeZone: !timeOnly})}
            </span>
            <DownChevronIcon color={color || 'dark'} slot="end" size="small" />
        </Button>
    );
};
